import { faqJsonLd } from "@amtana/browser";
import type { defineBrowserModule, SeoDeclaration } from "@amtana/browser";
import { shippedLanguage } from "@amtana/i18n/catalog";
import type { Phrase } from "@amtana/i18n/catalog";

import { familyBreadcrumb } from "./family-head.web.ts";
import type { UseCasePage, UseCaseWords } from "./features/use-cases/model/use-case-page.ts";
import { pageOfUseCase } from "./features/use-cases/model/use-case-pages.ts";
import { USE_CASES_WORDS_EN } from "./features/use-cases/model/use-cases-words-en.ts";
import { USE_CASES_WORDS_ES } from "./features/use-cases/model/use-cases-words-es.ts";

const FAMILY = "modules/site/browser/src/features/use-cases";

function pageAt(path: string): UseCasePage {
  const page = pageOfUseCase(path);
  if (!page) throw new Error(`No use-case page at "${path}": add it to USE_CASE_PAGES.`);
  return page;
}

function phraseOf(path: string, read: (words: UseCaseWords) => string): Phrase {
  const page = pageAt(path);
  return { kind: "phrase", read: (language) => read(page.words[shippedLanguage(language)]) };
}

/** A use-case page's title, in the active language. */
function titleOf(path: string): Phrase {
  return phraseOf(path, (words) => words.title);
}

/** A use-case page's search head: its job, its breadcrumb and the FAQ its body shows (ADR-034
 * points 6 and 7). A missing words file fails the test that reads `sources`. */
function seoOf(path: string): SeoDeclaration {
  const page = pageAt(path);
  return {
    description: phraseOf(path, (words) => words.description),
    keyphrase: phraseOf(path, (words) => words.keyphrase),
    contentType: "landing",
    jsonLd: (language) => {
      const shipped = shippedLanguage(language);
      const words = page.words[shipped];
      return [
        familyBreadcrumb(language, [
          { name: shipped === "es" ? USE_CASES_WORDS_ES.home : USE_CASES_WORDS_EN.home, path: "/" },
          { name: words.linkLabel, path: page.path },
        ]),
        faqJsonLd(words.questions),
      ];
    },
    sources: [
      `${FAMILY}/ui/blocks/use-case-block.tsx`,
      `${FAMILY}/model/${page.slug}-words-en.ts`,
      `${FAMILY}/model/${page.slug}-words-es.ts`,
    ],
  };
}

/** The use-cases family's screens (ADR-034 point 1). Keep a literal `.withScreens({ ... })`, never a
 * spread: the `screen-seo` policy reads this file as text. */
export function withUseCaseScreens(site: ReturnType<typeof defineBrowserModule>) {
  return site.withScreens({
    useCaseCompetitorFollowers: {
      path: "/use-cases/competitor-followers",
      title: titleOf("/use-cases/competitor-followers"),
      seo: seoOf("/use-cases/competitor-followers"),
      load: () => import("./features/use-cases/ui/sections/use-case-screen.tsx"),
    },
  });
}
